// src/pages/Dashboard/Admin/Inventaire/components/AddInventaireDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import { FaImage, FaSave, FaTimes } from "react-icons/fa";

import {
  DEFAULT_INVENTAIRE_PHOTO,
  getInventairePhotoSrc,
  toStockNumber,
} from "../utils/inventaireDashboardUtils";

const initialForm = {
  categorie: "",
  nom: "",
  stock: "",
  note: "",
  photo_url: "",
};

export default function AddInventaireDrawer({
  isOpen,
  onClose,
  onSubmit,
  saving,
  categoryOptions = [],
}) {
  const [formData, setFormData] = useState(initialForm);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setFormData(initialForm);
    setImgError(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const root = document.documentElement;
    const previousOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const onEscape = (event) => {
      if (event.key === "Escape" && !saving) {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onEscape);

    return () => {
      root.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onEscape);
    };
  }, [isOpen, saving, onClose]);

  const canSubmit = useMemo(() => {
    return (
      String(formData.categorie || "").trim() &&
      String(formData.nom || "").trim() &&
      String(formData.stock || "").trim() !== "" &&
      toStockNumber(formData.stock) >= 0 &&
      !saving
    );
  }, [formData, saving]);

  const previewSrc = useMemo(() => {
    if (imgError) return DEFAULT_INVENTAIRE_PHOTO;
    return getInventairePhotoSrc(formData.photo_url);
  }, [formData.photo_url, imgError]);

  const updateField = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: name === "stock" ? value.replace(",", ".") : value,
    }));

    if (name === "photo_url") {
      setImgError(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canSubmit) return;

    await onSubmit?.(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-2xl flex-col bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Nouvel objet
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              Ajouter à l’inventaire
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Renseignez la catégorie, le nom, le stock et éventuellement une
              photo ou une note.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            <FaTimes />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 space-y-5 overflow-y-auto p-5"
        >
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[220px,1fr]">
            <div className="space-y-3">
              <div className="relative flex h-44 items-center justify-center overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/60">
                <img
                  src={previewSrc}
                  alt="Aperçu inventaire"
                  onError={() => setImgError(true)}
                  className="h-full w-full object-contain"
                />

                <span className="absolute bottom-3 right-3 inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-sm dark:bg-white dark:text-slate-900">
                  <FaImage />
                </span>
              </div>

              <p className="text-xs leading-5 text-slate-500 dark:text-slate-400">
                Vous pouvez coller une URL d’image ou laisser vide. Une image
                par défaut sera utilisée.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Catégorie
                </label>

                <input
                  type="text"
                  value={formData.categorie}
                  onChange={(event) =>
                    updateField("categorie", event.target.value)
                  }
                  list="inventaire-categories"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                  required
                  autoFocus
                />

                <datalist id="inventaire-categories">
                  {categoryOptions.map((category) => (
                    <option key={category} value={category} />
                  ))}
                </datalist>
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Nom
                </label>

                <input
                  type="text"
                  value={formData.nom}
                  onChange={(event) => updateField("nom", event.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                  required
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Stock
                  </label>

                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={formData.stock}
                    onChange={(event) =>
                      updateField("stock", event.target.value)
                    }
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                    required
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Photo URL
                  </label>

                  <input
                    type="text"
                    value={formData.photo_url}
                    onChange={(event) =>
                      updateField("photo_url", event.target.value)
                    }
                    placeholder="https://... ou /uploads/..."
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                  />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Note
                </label>

                <textarea
                  value={formData.note}
                  onChange={(event) => updateField("note", event.target.value)}
                  rows={4}
                  placeholder="État, taille, localisation, informations utiles..."
                  className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                />
              </div>
            </div>
          </div>
        </form>

        <div className="border-t border-slate-200 bg-white/95 p-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            >
              <FaTimes />
              Annuler
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#012f6c] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaSave />
              {saving ? "Enregistrement..." : "Enregistrer"}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}